import type { AxiosResponse } from 'axios';

import api from 'api/api.config';

type ApiPromise<T = unknown> = Promise<AxiosResponse<T>>;

export interface StrapiMediaRef {
  url?: string;
  data?: { attributes?: { url?: string } };
  [key: string]: unknown;
}

export interface StrapiLessonAttributes {
  publishedAt?: string | null;
  name?: string;
  shortDescription?: string;
  content?: Array<{ id: string | number; type: string; [key: string]: unknown }>;
  [key: string]: unknown;
}

export interface StrapiRecord<TAttributes = Record<string, unknown>> {
  id: string | number;
  attributes: TAttributes;
}

export interface StrapiCourseAttributes {
  name?: string;
  coverDescription?: string;
  coverImage?: StrapiMediaRef;
  lessons?: { data: Array<StrapiRecord<StrapiLessonAttributes>> };
  content?: Array<{ id: string | number; type: string; [key: string]: unknown }>;
  [key: string]: unknown;
}

export interface StrapiCollectionResponse<TRecord = StrapiRecord> {
  data?: TRecord[];
}

export interface StrapiSingleResponse<TRecord = StrapiRecord> {
  data: TRecord;
}

//content
export const getArticles = (oktaUserId: string): ApiPromise<StrapiCollectionResponse> =>
  api.get(`/${oktaUserId}/cms/articles?populate=*`);

export const getArticle = (
  articleId: string | number,
  oktaUserId: string,
): ApiPromise<StrapiSingleResponse> =>
  api.get(
    `/${oktaUserId}/cms/articles/${articleId}?populate[0]=content.videoSrc.data.attributes.url&populate[1]=content.imageSrc.data.attributes.url&populate[2]=authorAvatar&populate[3]=linkedArticles&populate[4]=linkedWebinars&populate[5]=linkedDownloads&populate[6]=linkedGuides`,
  );

export const getGuides = (oktaUserId: string): ApiPromise<StrapiCollectionResponse> =>
  api.get(`/${oktaUserId}/cms/guides?populate=*`);

export const getGuide = (
  guideId: string | number,
  oktaUserId: string,
): ApiPromise<StrapiSingleResponse> =>
  api.get(
    `/${oktaUserId}/cms/guides/${guideId}?populate[0]=content.videoSrc.data.attributes.url&populate[1]=content.imageSrc.data.attributes.url&populate[2]=authorAvatar&populate[3]=linkedArticles&populate[4]=linkedWebinars&populate[5]=linkedDownloads&populate[6]=linkedGuides&populate[7]=guideIsLinkedTo`,
  );

export const getDownloads = (oktaUserId: string): ApiPromise<StrapiCollectionResponse> =>
  api.get(
    `/${oktaUserId}/cms/downloads?populate=*&sort=assetCreationDate:desc`,
  );

export const getDownload = (
  downloadId: string | number,
  oktaUserId: string,
): ApiPromise<StrapiSingleResponse> =>
  api.get(`/${oktaUserId}/cms/downloads/${downloadId}?populate=*`);

export const getWebinars = (oktaUserId: string): ApiPromise<StrapiCollectionResponse> =>
  api.get(`/${oktaUserId}/cms/webinars?populate=*&sort=webinarDate:desc`);

export const getWebinar = (
  webinarId: string | number,
  oktaUserId: string,
): ApiPromise<StrapiSingleResponse> =>
  api.get(
    `/${oktaUserId}/cms/webinars/${webinarId}?populate[0]=content.videoSrc.data.attributes.url&populate[1]=linkedArticles&populate[2]=linkedWebinars&populate[3]=linkedDownloads&populate[4]=linkedGuides&populate[5]=webinarIsLinkedTo&&populate[6]=content.imageSrc.data.attributes.url&&populate[7]=presenters.presenterAvatar.data.attributes.url&&populate[8]=content.file.data.attributes.url`,
  );

export const getCourses = (
  oktaUserId: string,
): ApiPromise<StrapiCollectionResponse<StrapiRecord<StrapiCourseAttributes>>> =>
  api.get(`/${oktaUserId}/cms/courses?populate=*&sort=publishDate:desc`);

export const getCourse = (
  courseId: string | number,
  oktaUserId: string,
): ApiPromise<StrapiSingleResponse<StrapiRecord<StrapiCourseAttributes>>> =>
  api.get(
    `${oktaUserId}/cms/courses/${courseId}?populate[0]=content&populate[1]=lessons`,
  );

export const getLesson = (
  lessonId: string | number,
  oktaUserId: string,
): ApiPromise<StrapiSingleResponse<StrapiRecord<StrapiLessonAttributes>>> =>
  api.get(
    `${oktaUserId}/cms/lessons/${lessonId}?populate[0]=content.videoSrc.data.attributes.url&populate[1]=content.imageSrc.data.attributes.url`,
  );

export const getReleaseNotes = (oktaUserId: string): ApiPromise<StrapiCollectionResponse> =>
  api.get(`/${oktaUserId}/cms/release-notes?populate=*`);

export const getReleaseNote = (
  releaseNoteId: string | number,
  oktaUserId: string,
): ApiPromise<StrapiSingleResponse> =>
  api.get(
    `/${oktaUserId}/cms/release-notes/${releaseNoteId}?populate[0]=content.videoSrc.data.attributes.url&populate[1]=linkedArticles&populate[2]=linkedWebinars&populate[3]=linkedDownloads&populate[4]=linkedGuides&populate[5]=webinarIsLinkedTo&&populate[6]=content.imageSrc.data.attributes.url&&populate[7]=presenters.presenterAvatar.data.attributes.url&&populate[8]=content.file.data.attributes.url`,
  );
